/**
 * Blog Post Table of Contents
 *
 * Parses headings from the blog post content, builds a navigable TOC list,
 * scrolls to sections on click, and highlights the currently visible section.
 */
class BlogPostTOC extends HTMLElement {
	/** @type {IntersectionObserver | null} */
	#observer = null;

	/** @type {Map<string, HTMLAnchorElement>} */
	#linkMap = new Map();

	/** @type {Set<string>} */
	#visibleIds = new Set();

	connectedCallback() {
		// Small delay so the blog content is fully rendered before we parse headings
		requestAnimationFrame(() => this.#init());
	}

	disconnectedCallback() {
		this.#observer?.disconnect();
	}

	#init() {
		const list = this.querySelector('[ref="list"]');
		if (!list) return;

		const selector = this.dataset.headingSelector || 'h2';
		if (!document.querySelector('.blog-post-content')) return;

		// Content headings plus any headings outside the content opted in via data-toc-heading (e.g. the FAQ)
		/** @type {NodeListOf<HTMLHeadingElement>} */
		const headings = document.querySelectorAll(`.blog-post-content :is(${selector}), [data-toc-heading]`);
		if (headings.length === 0) return;

		// Build the TOC list
		headings.forEach((heading, index) => {
			// Ensure each heading has an id for linking
			if (!heading.id) {
				heading.id = `toc-heading-${index}`;
			}

			const li = document.createElement('li');
			li.classList.add('blog-post-toc__item');

			const a = document.createElement('a');
			a.href = `#${heading.id}`;
			a.textContent = heading.textContent?.trim() ?? '';
			a.classList.add('blog-post-toc__link');

			a.addEventListener('click', (e) => {
				e.preventDefault();
				this.#scrollTo(heading);
			});

			this.#linkMap.set(heading.id, a);

			li.appendChild(a);
			list.appendChild(li);
		});

		// Set up IntersectionObserver to track visible headings
		this.#observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const id = entry.target.id;
					if (entry.isIntersecting) {
						this.#visibleIds.add(id);
					} else {
						this.#visibleIds.delete(id);
					}
				});
				this.#updateActiveLink(headings);
			},
			{
				rootMargin: '-80px 0px -60% 0px',
				threshold: 0,
			}
		);

		headings.forEach((heading) => this.#observer?.observe(heading));
	}

	/**
	 * Update the active link based on visible headings.
	 * Picks the first visible heading in document order; if none are visible,
	 * keeps the last active one.
	 * @param {NodeListOf<HTMLHeadingElement>} headings
	 */
	#updateActiveLink(headings) {
		// Find first visible heading in DOM order
		let activeId = null;
		for (const heading of headings) {
			if (this.#visibleIds.has(heading.id)) {
				activeId = heading.id;
				break;
			}
		}

		if (!activeId) return;

		this.#linkMap.forEach((link, id) => {
			link.classList.toggle('blog-post-toc__link--active', id === activeId);
		});
	}

	/**
	 * Smooth-scroll to a heading and update the URL hash.
	 * @param {HTMLElement} heading
	 */
	#scrollTo(heading) {
		const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '60', 10);
		const top = heading.getBoundingClientRect().top + window.scrollY - headerOffset - 24;

		window.scrollTo({ top, behavior: 'smooth' });
		history.replaceState(null, '', `#${heading.id}`);
	}
}

if (!customElements.get('blog-post-toc')) {
	customElements.define('blog-post-toc', BlogPostTOC);
}
